import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Users, Target, Globe } from 'lucide-react';
import { Button, Card, Reveal } from '../components/common';

const AboutPage: React.FC = () => {
  const team = [
    {
      name: 'Tinashe Kevin',
      role: 'Founder & Owner',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop',
      bio: 'Visionary entrepreneur passionate about sustainable agriculture and connecting farmers with urban communities.',
    },
    {
      name: 'Michael Chen',
      role: 'Head of Operations',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d',
      bio: 'Expert in supply chain logistics and quality control.',
    },
    {
      name: 'Emma Rodriguez',
      role: 'Sustainability Director',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80',
      bio: 'Passionate about eco-friendly farming practices.',
    },
    {
      name: 'David Okafor',
      role: 'Farm Relations Manager',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e',
      bio: 'Connects directly with local farmers across Zimbabwe.',
    },
  ];

  const values = [
    {
      icon: Leaf,
      title: 'Sustainability First',
      description: 'We prioritize eco-friendly practices and support regenerative agriculture.',
    },
    {
      icon: Users,
      title: 'Community Focused',
      description: 'Empowering local farmers and supporting rural communities.',
    },
    {
      icon: Target,
      title: 'Quality Guaranteed',
      description: 'Every product meets our strict freshness and quality standards.',
    },
    {
      icon: Globe,
      title: 'Local Impact',
      description: 'Reducing food miles by sourcing directly from nearby farms.',
    },
  ];

  return (
    <div className="bg-dark-dark dark:bg-white transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent via-accent-light to-primary opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              About Angel Cite
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Connecting local farms directly to your table. Fresh, organic, and sustainably sourced.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <Reveal animation="slide-right">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white dark:text-dark-dark mb-6 transition-colors duration-300">
                  Our Story
                </h2>
                <div className="space-y-4 text-gray-300 dark:text-gray-600 transition-colors duration-300">
                  <p>
                    Angel Cite was founded and is owned by <strong className="text-accent dark:text-primary">Tinashe Kevin</strong>. 
                    Established in 2023 in Harare, our journey began with a simple yet powerful vision: 
                    make fresh, organic farm produce easily accessible to everyone while supporting local farmers.
                  </p>
                  <p>
                    Tinashe noticed that despite Zimbabwe's rich agricultural heritage, many urban residents 
                    struggled to access fresh, quality produce while local farmers faced challenges 
                    reaching broader markets. This realization sparked the creation of Angel Cite.
                  </p>
                  <p>
                    Today, under Tinashe's leadership, we connect over 100 local farms directly to thousands 
                    of happy customers, ensuring freshness, supporting communities, and promoting 
                    sustainable agriculture across Zimbabwe.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal animation="slide-left">
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1574943320219-553eb213f72d"
                    alt="Farmers working in field"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-12 md:py-20 bg-dark-light dark:bg-gray-50 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal animation="slide-up">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
                Our Values
              </h2>
              <p className="text-gray-300 dark:text-gray-600 max-w-2xl mx-auto transition-colors duration-300">
                The principles that guide everything we do at Angel Cite
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((value, index) => (
              <Reveal key={index} animation="slide-up" delay={index * 100}>
                <Card className="text-center p-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-accent/20 dark:bg-primary/20 rounded-full flex items-center justify-center transition-colors duration-300">
                    <value.icon className="w-8 h-8 text-accent dark:text-primary transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-white dark:text-dark-dark mb-2 transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-gray-300 dark:text-gray-500 text-sm transition-colors duration-300">
                    {value.description}
                  </p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Our Team */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal animation="slide-up">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
                Meet Our Team
              </h2>
              <p className="text-gray-300 dark:text-gray-600 max-w-2xl mx-auto transition-colors duration-300">
                Passionate individuals dedicated to revolutionizing how Zimbabwe eats
              </p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {team.map((member, index) => (
              <Reveal key={member.name} animation="scale" delay={index * 100}>
                <Card hover className="text-center p-6">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-dark-light dark:border-gray-100">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-white dark:text-dark-dark mb-1 transition-colors duration-300">
                    {member.name}
                  </h3>
                  <p className="text-accent dark:text-primary text-sm font-medium mb-3 transition-colors duration-300">
                    {member.role}
                  </p>
                  <p className="text-gray-300 dark:text-gray-500 text-sm transition-colors duration-300">
                    {member.bio}
                  </p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 md:py-20 bg-dark-light dark:bg-gray-50 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { value: '100+', label: 'Local Farm Partners' },
              { value: '5000+', label: 'Happy Customers' },
              { value: '50+', label: 'Fresh Products' },
              { value: '24h', label: 'Delivery Time' },
            ].map((stat, index) => (
              <Reveal key={stat.label} animation="scale" delay={index * 100}>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-accent dark:text-primary mb-2 transition-colors duration-300">
                    {stat.value}
                  </div>
                  <div className="text-gray-300 dark:text-gray-600 text-sm md:text-base transition-colors duration-300">
                    {stat.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal animation="slide-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white dark:text-dark-dark mb-6 transition-colors duration-300">
              Join Our Fresh Food Movement
            </h2>
            <p className="text-gray-300 dark:text-gray-600 text-lg mb-8 max-w-2xl mx-auto transition-colors duration-300">
              Experience the difference of farm-fresh produce while supporting local agriculture.
            </p>
          </Reveal>
          <Reveal animation="fade-in" delay={200}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="primary"
                size="lg"
                as={Link}
                to="/products"
              >
                Shop Fresh Produce
              </Button>
              <Button
                variant="outline"
                size="lg"
                as={Link}
                to="/contact"
                className="border-accent dark:border-primary text-accent dark:text-primary hover:bg-accent dark:hover:bg-primary hover:text-white"
              >
                Get In Touch
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
